import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { formatShoppingDate, listsApi, type DaySummary } from '../lib/domain';
import { ApiError } from '../lib/api';
import { ProblemItems } from '../components/ProblemItems';
import { useScope } from '../lib/hooks';

// Übersicht aller Einkaufslisten eines Tages einer Gruppe mit Tagesabschluss.
export function DaySummaryPage() {
  const { date } = useParams<{ date: string }>();
  const [search] = useSearchParams();
  const [activeScope] = useScope();
  const scope = search.get('scope') ?? activeScope;
  const [day, setDay] = useState<DaySummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [includeMemo, setIncludeMemo] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!date || !scope) return;
    try {
      setDay((await listsApi.daySummary(scope, date)).day);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Die Tagesübersicht konnte nicht geladen werden.');
    }
  }, [date, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  async function closeDay() {
    if (!date || !day) return;
    if (day.openListCount > 0 && !confirm(`Es gibt noch ${day.openListCount} offene Liste(n). Trotzdem alle abschließen und den Tag beenden?`)) return;
    setBusy(true);
    setError(null);
    try {
      setDay((await listsApi.closeDay(scope, date, day.openListCount > 0)).day);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tagesabschluss fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function distribute() {
    if (!date) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await listsApi.distributeDay(scope, date, includeMemo);
      setDay(res.day);
      setNotice(`${res.distributed} Produkt(e) verteilt${includeMemo ? `, ${res.memoMoved} aus der Merkliste` : ''}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verteilen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function assign(dayProductId: string, listId: string) {
    if (!listId) return;
    setError(null);
    try {
      await listsApi.assignDayProduct(dayProductId, listId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Zuordnung fehlgeschlagen.');
    }
  }

  async function removeProduct(dayProductId: string) {
    setError(null);
    try {
      await listsApi.removeDayProduct(dayProductId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen.');
    }
  }

  if (!day) return error ? <p className="error-text">{error}</p> : <p>Lade…</p>;
  const allProblems = day.lists.reduce((n, e) => n + e.summary.problemItems.length, 0);

  return (
    <div className="stack">
      <div className="row between">
        <h1>Einkaufstag {formatShoppingDate(day.date)}</h1>
        <span className={`badge ${day.closure ? 'ok' : 'warn'}`}>{day.closure ? 'Abgeschlossen' : 'Offen'}</span>
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}

      <div className="card row between">
        <div>
          <strong>{Number(day.totalAmount).toFixed(2)} €</strong>
          <div className="muted">Gesamtsumme des Tages</div>
        </div>
        <div>
          <div>{day.lists.length} Liste(n)</div>
          <div className="muted">{allProblems} Artikel nicht (vollständig) erhältlich</div>
        </div>
      </div>

      {notice && <p role="status">{notice}</p>}

      <section className="card stack">
        <h2>Produkte des Tages</h2>
        {day.products.length === 0 ? (
          <p className="muted">Noch keine Produkte. In der Produktliste markierte Produkte lassen sich dem Tag zuordnen.</p>
        ) : (
          <ul className="list-reset stack">
            {day.products.map((dp) => {
              const list = day.lists.find((e) => e.list.id === dp.listId)?.list;
              return (
                <li key={dp.id} className="row between">
                  <div>
                    <strong>{dp.product.name}</strong>
                    <div className="muted">
                      {dp.product.defaultStoreChain ? `Standard: ${dp.product.defaultStoreChain.name}` : 'Kein Standardgeschäft'}
                    </div>
                  </div>
                  {list ? (
                    <Link to={`/lists/${list.id}`}>✓ {list.name || 'Einkaufsliste'}</Link>
                  ) : (
                    <div className="row">
                      <select value="" onChange={(e) => void assign(dp.id, e.target.value)} aria-label={`${dp.product.name} einer Liste zuordnen`}>
                        <option value="">Liste wählen…</option>
                        {day.lists
                          .filter((e) => e.list.status === 'OPEN')
                          .map((e) => (
                            <option key={e.list.id} value={e.list.id}>
                              {e.list.name || 'Einkaufsliste'}
                              {e.list.storeBranch ? ` (${e.list.storeBranch.chain.name})` : ''}
                            </option>
                          ))}
                      </select>
                      <button onClick={() => void removeProduct(dp.id)} aria-label={`${dp.product.name} entfernen`}>×</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!day.closure && (
          <div className="row">
            <button className="primary" onClick={() => void distribute()} disabled={busy}>
              Alle Produkte verteilen
            </button>
            <label>
              <input type="checkbox" checked={includeMemo} onChange={(e) => setIncludeMemo(e.target.checked)} /> Merkliste mit verteilen
            </label>
          </div>
        )}
      </section>

      {day.lists.length === 0 && <p className="muted">Für diesen Tag gibt es keine Listen.</p>}
      {day.lists.map(({ list, summary }) => (
        <section key={list.id} className="stack">
          <div className="row between">
            <h2>
              <Link to={`/lists/${list.id}/summary`}>{list.name || 'Einkaufsliste'}</Link>
            </h2>
            <span className={`badge ${list.status === 'ARCHIVED' ? 'ok' : 'warn'}`}>{list.status === 'ARCHIVED' ? 'Abgeschlossen' : 'Offen'}</span>
          </div>
          <p className="muted">
            {list.storeBranch ? `${list.storeBranch.chain.name} - ${list.storeBranch.name}` : 'Ohne Filiale'} · {Number(summary.totalAmount).toFixed(2)} €
          </p>
          <ProblemItems items={summary.problemItems} scope={scope} />
        </section>
      ))}

      {day.closure ? (
        <p className="muted">Abgeschlossen am {new Date(day.closure.closedAt).toLocaleString('de-DE')}.</p>
      ) : (
        day.lists.length > 0 && (
          <button className="primary" onClick={closeDay} disabled={busy}>
            {busy ? 'Schließt ab…' : 'Tag abschließen'}
          </button>
        )
      )}
      <Link to="/lists">← Zu den Listen</Link>
    </div>
  );
}
